import React from "react";
import Head from "next/head";
import { JobListing } from "@lib/types/jobTypes";
import getJobUrl from "@lib/utils/url/getJobUrl";
import stripHtmlTags from "@lib/utils/string/stripHtmlTags";
import shortenToSentenceEndOrLength from "@lib/utils/string/shortenToSentenceEndOrLength";
import StructuredDataTag from "../Various/Seo/StructuredDataTag";

const JOB_SALARY_TYPE = {
  1: "FIXED",
  2: "RANGE",
  3: "NONE",
};

function StructuredData({ job }: { job: JobListing }) {
  const jobStructuredData: { [key: string]: any } = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    datePosted: job.published_at ? `${job.published_at} 00:00:00` : null,
    validThrough: job.application_end,
    title: job.title,
    hiringOrganization: {
      "@context": "https://schema.org/",
      "@type": "Organization",
      name: job.company.name,
      sameAs: job.company.website,
      logo: job.company.logo,
      description: job.company.description,
    },
    directApply: true,
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        streetAddress: job.job_address?.address_line_1,
        addressLocality: job.job_address?.city,
        addressRegion: job.job_address?.state,
        addressCountry: job.job_address?.country_code,
        postalCode: job.job_address?.zip_code,
      },
    },
    description: job.description,
    url: getJobUrl(job),
    employmentType: job.job_type?.google_type,
    industry: job.job_industry?.title_translations?.en,
  };

  const salaryModeIndex = Number(
    job.job_salary?.mode
  ) as keyof typeof JOB_SALARY_TYPE;
  const salaryMode = JOB_SALARY_TYPE[salaryModeIndex];

  if (salaryMode === "FIXED") {
    jobStructuredData.baseSalary = {
      "@type": "MonetaryAmount",
      currency: job.job_salary?.currency?.code,
      value: {
        "@type": "QuantitativeValue",
        unitText: job.job_salary?.period?.title_translations?.de.toUpperCase(),
        value: job.job_salary?.gross,
      },
    };
  } else if (salaryMode === "RANGE") {
    jobStructuredData.baseSalary = {
      "@type": "MonetaryAmount",
      currency: job.job_salary?.currency?.code,
      value: {
        "@type": "QuantitativeValue",
        unitText: job.job_salary?.period?.title_translations?.de.toUpperCase(),
        minValue: job.job_salary?.from,
        maxValue: job.job_salary?.to,
      },
    };
  }

  return (
    <>
      <StructuredDataTag data={jobStructuredData} />
      <Head>
        <meta
          name="description"
          key="description"
          content={
            job.short_description?.length
              ? job.short_description
              : shortenToSentenceEndOrLength(stripHtmlTags(job.description))
          }
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@smarketer_de" />
        <meta property="og:url" content={getJobUrl(job)} />
        <meta property="og:title" content={job.title} />
        <meta
          property="og:description"
          content={shortenToSentenceEndOrLength(stripHtmlTags(job.description))}
        />
      </Head>
    </>
  );
}

export default StructuredData;
